import { z } from 'zod';
import { baseString } from './core/primitives';
import { str, emptyToUndefined, booleanFlag } from './core/helpers';

// ── Constants ───────────────────────────────────────────────────────────────
const SUBFORM_NAME_MAX_LENGTH = 255;
const SUBFORM_DESCRIPTION_MAX_LENGTH = 2000;

// ── Field definitions ───────────────────────────────────────────────────────

export const subformNameField = (t) =>
  z.preprocess(
    str,
    baseString
      .min(1, t('validation.subform.name.required'))
      .max(
        SUBFORM_NAME_MAX_LENGTH,
        t('validation.subform.name.maxLength', { max: SUBFORM_NAME_MAX_LENGTH })
      )
  );

export const subformDescriptionField = (t) =>
  emptyToUndefined(
    baseString
      .max(
        SUBFORM_DESCRIPTION_MAX_LENGTH,
        t('validation.subform.description.maxLength', {
          max: SUBFORM_DESCRIPTION_MAX_LENGTH,
        })
      )
      .optional()
  );

export const templateField = (t) =>
  booleanFlag(t('validation.subform.template.invalid'));

export const jsonObjectField = (t) =>
  z.preprocess(
    (val) => {
      if (val === undefined || val === null) return undefined;
      return val;
    },
    z
      .union([z.string(), z.object({}).passthrough()])
      .refine(
        (val) => {
          try {
            const parsed = typeof val === 'string' ? JSON.parse(val) : val;
            return (
              parsed &&
              typeof parsed === 'object' &&
              Array.isArray(parsed.fields) &&
              parsed.fields.length > 0
            );
          } catch {
            return false;
          }
        },
        { message: t('validation.subform.fields.required') }
      )
      .optional()
  );

// ── Schemas ─────────────────────────────────────────────────────────────────

export const createSubformSchema = (t) =>
  z.object({
    name: subformNameField(t),
    description: subformDescriptionField(t),
    template: templateField(t).optional(),
    jsonObject: jsonObjectField(t),
  });

export const updateSubformSchema = (t) =>
  z.object({
    name: subformNameField(t).optional(),
    description: subformDescriptionField(t),
    template: templateField(t).optional(),
    jsonObject: jsonObjectField(t),
  });

export { SUBFORM_NAME_MAX_LENGTH, SUBFORM_DESCRIPTION_MAX_LENGTH };
